import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { WorkflowService } from '../../../core/services/workflow.service';
import { SchemaApiService } from '../../../core/services/schema-api.service';
import { WorkflowDefinitionDto } from '../../../core/models/workflow.model';

@Component({
      selector: 'app-workflow-designer',
      standalone: true,
      imports: [CommonModule, ReactiveFormsModule],
      templateUrl: './workflow-designer.component.html'
})
export class WorkflowDesignerComponent implements OnInit {
      // UI State Management
      workflows: any[] = [];
      wfForm: FormGroup;
      showDesigner = false; // Controls Modal Visibility
      isEditMode = false;
      isLoading = true;

      selectedOrgName: string = '';

      totalCount = 0;
      pageIndex = 0;
      pageSize = 5;
      searchTerm = '';

      masterActionList: string[] = [];
      masterLabels: string[] = [];

      // for  a state
      masterStateCodes: string[] = [];
      masterStateLabels: string[] = [];
      masterRoleCodes: string[] = [];
      orgSuggestions: any[] = [];

      constructor(
            private fb: FormBuilder,
            private wfService: WorkflowService,
            private org: SchemaApiService
      ) {
            this.wfForm = this.initForm();
      }

      ngOnInit() {
            this.getMasterList();
            this.refreshList();
      }

      refreshList() {
            this.isLoading = true;

            // Call the service with current page, size, and search term
            this.wfService.getAll(this.pageIndex, this.pageSize, this.searchTerm).subscribe({
                  next: (result) => {
                        // result is of type PagedResult<WorkflowDefinitionDto>
                        this.workflows = result.items;
                        this.totalCount = result.totalCount;
                        this.isLoading = false;

                        // --- NEW: Extract unique values for dropdowns ---
                        //this.extractMasterData(result.items);
                  },
                  error: (err) => {
                        console.error('Fetch error:', err);
                        this.isLoading = false;
                  }
            });
      }

      getMasterList(){
            this.isLoading = true;

            // Call the service with current page, size, and search term
            this.wfService.getMasterData().subscribe({
                  next: (result) => {
                        // --- NEW: Extract unique values for dropdowns ---
                        this.extractMasterData(result);
                  },
                  error: (err) => {
                        console.error('Fetch error:', err);
                        this.isLoading = false;
                  }
            });
      }

      extractMasterData(data: WorkflowDefinitionDto) {
            const actionSet = new Set<string>();
            const labelSet = new Set<string>();
            const stateCodeSet = new Set<string>();
            const stateLabelSet = new Set<string>();
            const roleSet = new Set<string>(); // New set for roles

            
            data.actions?.forEach((a: any) => {
                  if (a.code) actionSet.add(a.code);
                  if (a.label) labelSet.add(a.label);
            });

            // Collect States
                  data.states?.forEach((s: any) => {
                        if (s.code) stateCodeSet.add(s.code);
                        if (s.label) stateLabelSet.add(s.label);
                  });

            data.actions?.forEach((a: any) => {
                  if (a.code) actionSet.add(a.code);
                  if (a.label) labelSet.add(a.label);
            });

            // Collect Roles from transitions
                  data.roles?.forEach((t: any) => {
                        if (t.code) roleSet.add(t.code);
                  });

            // data.forEach(wf => {
            //       wf.actions?.forEach((a: any) => {
            //             if (a.code) actionSet.add(a.code);
            //             if (a.label) labelSet.add(a.label);
            //       });
            //       // Collect States
            //       wf.states?.forEach((s: any) => {
            //             if (s.code) stateCodeSet.add(s.code);
            //             if (s.label) stateLabelSet.add(s.label);
            //       });
            //       // Collect Roles from transitions
            //       wf.transitions?.forEach((t: any) => {
            //             if (t.roleCode) roleSet.add(t.roleCode);
            //       });
            // });

            this.masterActionList = Array.from(actionSet).sort();
            this.masterLabels = Array.from(labelSet).sort();

            // New State lists
            this.masterStateCodes = Array.from(stateCodeSet).sort();
            this.masterStateLabels = Array.from(stateLabelSet).sort();
            this.masterRoleCodes = Array.from(roleSet).sort();
      }

      onSearch(value: string) {
            this.searchTerm = value;
            this.isLoading = true;
      }

      onPageChange(newIndex: number) {
            this.pageIndex = newIndex;
            this.refreshList();
      }

      get totalPages(): number {
            return Math.ceil(this.totalCount / this.pageSize);
      }
      goToPage(index: number) {
            this.pageIndex = index;
            this.refreshList();
      }
      changePageSize(event: any) {
            this.pageSize = +event.target.value; // Convert string to number
            this.pageIndex = 0;                  // Reset to first page
            this.refreshList();
      }

      initForm(): FormGroup {
            return this.fb.group({
                  id: [null],
                  orgId: ['ORG-7721', Validators.required], // Default Org ID
                  entity: ['', Validators.required],
                  name: ['', Validators.required],
                  version: [1, [Validators.required, Validators.min(1)]],
                  isActive: [true],
                  actions: this.fb.array([]),
                  states: this.fb.array([]),
                  transitions: this.fb.array([])
            });
      }

      // --- Modal Logic --- //

      openDesigner(workflow?: any) {
            this.wfForm.reset();
            this.actions.clear();
            this.states.clear();
            this.transitions.clear();
            this.orgSuggestions = [];

            if (workflow) {
                  this.isEditMode = true;
                  this.patchWorkflowData(workflow);
            } else {
                  this.isEditMode = false;
                  this.selectedOrgName = ''; // Reset for new entry
                  const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
                  this.wfForm.patchValue({ orgId: ZERO_GUID, version: 1, isActive: true });
            }
            this.showDesigner = true;
      }
      patchWorkflowData(data: any) {
            // console.log("Patching data:", data);
            // this.selectedOrgName = data.orgName || data.OrgName || 'Unknown Organisation';
            this.selectedOrgName = data.orgName || 'Unknown Organisation';
            this.wfForm.patchValue({
                  id: data.id,
                  orgId: data.orgId,
                  entity: data.entity,
                  name: data.name,
                  version: data.version,
                  isActive: data.isActive
            });


            data.actions?.forEach((a: any) => this.actions.push(this.fb.group(a)));
            data.states?.forEach((s: any) => this.states.push(this.fb.group(s)));
            data.transitions?.forEach((t: any) => this.transitions.push(this.fb.group(t)));
      }

      // --- Form Array Getters ---
      get actions() { return this.wfForm.get('actions') as FormArray; }
      get states() { return this.wfForm.get('states') as FormArray; }
      get transitions() { return this.wfForm.get('transitions') as FormArray; }

      // --- Row Management ---
      addAction() {
            this.actions.push(this.fb.group({
                  code: ['', Validators.required],
                  label: ['', Validators.required],
                  requiresComment: [false]
            }));
      }

      addState() {
            this.states.push(this.fb.group({
                  code: ['', Validators.required],
                  label: ['', Validators.required],
                  isInitial: [false],
                  isFinal: [false],
                  isEditable: [true],
                  allowPrint: [true],
                  seq: [this.states.length + 1]
            }));
      }

      addTransition() {
            this.transitions.push(this.fb.group({
                  fromState: ['', Validators.required],
                  actionCode: ['', Validators.required],
                  toState: ['', Validators.required],
                  roleCode: ['ADMIN', Validators.required],
                  conditionExpr: [''],
                  priority: [1]
            }));
      }

      removeRow(array: FormArray, index: number) {
            array.removeAt(index);
      }


      onSave() {
            // The Zero GUID string required for .NET Guid parsing
            const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

            const formValue = this.wfForm.value;

            // Construct the payload to match WorkflowDefinitionDto exactly
            const payload = {
                  // Spread existing form values (Name, Entity, Version, IsActive)
                  ...formValue,

                  // Ensure Id is a valid GUID string; use ZERO_GUID for new records
                  id: (this.isEditMode && formValue.id) ? formValue.id : ZERO_GUID,

                  // OrgId MUST be a valid GUID string and cannot be an empty string
                  orgId: (formValue.orgId && formValue.orgId.trim() !== '') ? formValue.orgId : ZERO_GUID,

                  // Ensure these match the List names in your C# DTO exactly (case-sensitive)
                  actions: formValue.actions || [],
                  states: formValue.states || [],
                  transitions: formValue.transitions || []
            };

            this.wfService.save(payload).subscribe({
                  next: (res) => {
                        this.showDesigner = false;
                        this.refreshList();
                  },
                  error: (err) => {
                        // Detailed log to see exactly which field the server rejects
                        console.error("Server Validation Error:", err.error?.errors || err.error);
                        alert("Save Failed: Please check the console for field errors.");
                  }
            });
      }
      onDelete() {
            const id = this.wfForm.get('id')?.value;
            if (id && confirm("Delete this workflow permanently?")) {
                  this.wfService.delete(id).subscribe(() => {
                        this.showDesigner = false;
                        this.refreshList();
                  });
            }
      }
      onOrgSearch(event: any) {
            const query = event.target.value;
            if (!query || query.length < 2) {
                  this.orgSuggestions = [];
                  return;
            }

            this.org.getOrgSuggestions(query).subscribe({
                  next: (res: any) => {
                        // Assuming res is an array of { id: string, name: string }
                        this.orgSuggestions = res;
                  },
                  error: (err) => console.error("Lookup error", err)
            });
      }

      selectOrg(org: any) {
            // 1. Update the actual form value with the UUID
            this.wfForm.patchValue({ orgId: org.id });

            // 2. Update the UI display variable
            this.selectedOrgName = org.name;

            // 3. Close the dropdown
            this.orgSuggestions = [];

            // Mark the form as "dirty" so the save button enables if needed
            this.wfForm.get('orgId')?.markAsDirty();
      }
}